import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGamesBase } from '../../utils/gamesBase';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import { generatePPTTaskSet } from '../../utils/pptGenerator';

// ============== Bilinear interpolation on a quadrilateral ==============
const mapToQuad = (u, v, tl, tr, br, bl) => {
  const x = (1 - v) * ((1 - u) * tl[0] + u * tr[0]) + v * ((1 - u) * bl[0] + u * br[0]);
  const y = (1 - v) * ((1 - u) * tl[1] + u * tr[1]) + v * ((1 - u) * bl[1] + u * br[1]);
  return [x, y];
};

// ============== 3D Cube — Cabinet / Oblique Projection ==============
//
// Matches the DLR PPT reference images exactly:
//   • Front face = large, nearly square (left side of cube)
//   • Right face = narrow parallelogram receding up-right at ~45°
//   • Top face   = narrow parallelogram receding up-right at ~45°
//
// Cabinet projection: receding edges are drawn at HALF the true length,
// at a 45° angle going up and to the right.
//
// ViewBox: 0 0 160 130
//
// Front face size = 100 × 100
// Receding depth  = 40 (half of 100, at 45°) → dx=28, dy=−28
//
//       A ────────── B                A = front-TL + offset
//      /            / |               B = front-TR + offset
//     /            /  |               C = front-TR
//    D ────────── C   |               D = front-TL
//    |            |   F               E = front-BL
//    |   front    |  /                F = front-BR + offset
//    |            | /                 G = front-BR
//    E ────────── G
//
const FRONT = 100;   // front face edge length
const DEPTH = 40;    // foreshortened receding depth
const OX = 28;       // DEPTH * cos(45°) ≈ 28
const OY = 28;       // DEPTH * sin(45°) ≈ 28
const PAD = 5;       // padding inside viewBox

// Front face corners (large square on the left)
const D = [PAD,          PAD + OY];          // front top-left
const C = [PAD + FRONT,  PAD + OY];          // front top-right
const E = [PAD,          PAD + OY + FRONT];  // front bottom-left
const G = [PAD + FRONT,  PAD + OY + FRONT];  // front bottom-right

// Receding corners (offset up-right by OX, OY)
const A = [D[0] + OX, D[1] - OY];  // back top-left
const B = [C[0] + OX, C[1] - OY];  // back top-right
const F = [G[0] + OX, G[1] - OY];  // back bottom-right

const VB_W = PAD + FRONT + OX + PAD;  // viewBox width
const VB_H = PAD + OY + FRONT + PAD;  // viewBox height

const pts = (arr) => arr.map(p => `${p[0]},${p[1]}`).join(' ');

const Cube3D = ({ cube, size = 130, selected = false, onClick }) => {
  const dr = 4.5; // dot radius in viewBox units

  // Shrink quad slightly so dots don't clip face edges
  const inset = (corners, amt = 0.12) => {
    const cx = corners.reduce((s, c) => s + c[0], 0) / 4;
    const cy = corners.reduce((s, c) => s + c[1], 0) / 4;
    return corners.map(c => [c[0] + (cx - c[0]) * amt, c[1] + (cy - c[1]) * amt]);
  };

  // Quad corners in [TL, TR, BR, BL] order for each face
  // Front face (the big square): TL=D, TR=C, BR=G, BL=E
  const frontQ = inset([D, C, G, E]);
  // Top face (narrow parallelogram): TL=A, TR=B, BR=C, BL=D
  const topQ   = inset([A, B, C, D]);
  // Right face (narrow parallelogram): TL=C, TR=B, BR=F, BL=G
  const rightQ = inset([C, B, F, G]);

  const renderDots = (dots, quad, prefix) =>
    dots.map((dot, i) => {
      const [cx, cy] = mapToQuad(dot[0], dot[1], quad[0], quad[1], quad[2], quad[3]);
      return <circle key={`${prefix}${i}`} cx={cx} cy={cy} r={dr} fill="black" />;
    });

  const aspect = VB_H / VB_W;

  return (
    <div
      className={`relative cursor-pointer transition-all ${selected ? 'scale-110' : 'hover:scale-105'}`}
      style={{ width: size, height: size * aspect }}
      onClick={onClick}
    >
      <svg
        width={size}
        height={size * aspect}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
      >
        {/* Front face (large square — drawn first, behind) */}
        <polygon points={pts([D, C, G, E])} fill="white" stroke="black" strokeWidth="2" strokeLinejoin="round" />
        {renderDots(cube.front.dots, frontQ, 'f')}

        {/* Right face (narrow parallelogram) */}
        <polygon points={pts([C, B, F, G])} fill="white" stroke="black" strokeWidth="2" strokeLinejoin="round" />
        {renderDots(cube.right.dots, rightQ, 'r')}

        {/* Top face (narrow parallelogram — drawn last, on top) */}
        <polygon points={pts([D, A, B, C])} fill="white" stroke="black" strokeWidth="2" strokeLinejoin="round" />
        {renderDots(cube.top.dots, topQ, 't')}

        {/* Outer silhouette for crispness */}
        <polygon points={pts([A, B, F, G, E, D])} fill="none" stroke="black" strokeWidth="2" strokeLinejoin="round" />
        {/* Inner edges */}
        <line x1={D[0]} y1={D[1]} x2={C[0]} y2={C[1]} stroke="black" strokeWidth="2" />
        <line x1={C[0]} y1={C[1]} x2={B[0]} y2={B[1]} stroke="black" strokeWidth="2" />
        <line x1={C[0]} y1={C[1]} x2={G[0]} y2={G[1]} stroke="black" strokeWidth="2" />
      </svg>
      {selected && (
        <div className="absolute inset-0 border-4 border-blue-500 rounded-lg pointer-events-none" />
      )}
    </div>
  );
};

// ============== Unfolded Cube Pattern ==============
const UnfoldedCube = ({ pattern, size = 60 }) => {
  const rows = pattern._rows || 3;
  const cols = pattern._cols || 4;
  const faceNames = ['front', 'right', 'top', 'back', 'left', 'bottom'];

  const grid = Array.from({ length: rows }, () => Array(cols).fill(null));
  faceNames.forEach(name => {
    const face = pattern[name];
    if (face && face.gridRow !== undefined) {
      grid[face.gridRow][face.gridCol] = face;
    }
  });

  const dotR = size * 0.09;

  return (
    <div className="inline-block">
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, ${size}px)`, gridTemplateRows: `repeat(${rows}, ${size}px)` }}>
        {grid.flat().map((face, idx) => {
          if (!face) return <div key={idx} style={{ width: size, height: size }} />;
          return (
            <div
              key={idx}
              className="bg-white relative"
              style={{ width: size, height: size, border: '1.5px solid black' }}
            >
              {face.dots.map((dot, i) => (
                <div
                  key={i}
                  className="absolute bg-black rounded-full"
                  style={{
                    width: dotR,
                    height: dotR,
                    left: `${dot[0] * 100}%`,
                    top: `${dot[1] * 100}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============== Main Game Component ==============
// The five answer cubes sit in one row; shrink them so the row fits narrow phones
// (page padding 2×16px, four 8px gaps, plus a little breathing room at the edges),
// never below the 48px answer buttons.
const getOptionSize = () => Math.max(52, Math.min(120, Math.floor((window.innerWidth - 32 - 4 * 8 - 16) / 5)));

const PPTGame = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const gamesBase = useGamesBase();
  const location = useLocation();
  const { level, totalTasks, timeLimit, subject } = location.state || {};

  const [tasks, setTasks] = useState([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(timeLimit ? timeLimit * 60 : null);
  const [showTaskNumber, setShowTaskNumber] = useState(true);
  const [optionSize, setOptionSize] = useState(getOptionSize);

  useEffect(() => {
    const onResize = () => setOptionSize(getOptionSize());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate tasks on mount
  useEffect(() => {
    const generatedTasks = generatePPTTaskSet(totalTasks || 20);
    setTasks(generatedTasks);
    setGameStartTime(Date.now());
  }, [totalTasks]);

  // Timer countdown
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleSubmitGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Show task number for 1 second before each task
  useEffect(() => {
    if (currentTaskIndex >= tasks.length) return;
    
    setShowTaskNumber(true);
    const timer = setTimeout(() => {
      setShowTaskNumber(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentTaskIndex, tasks.length]);

  const handleAnswerSelect = (answerIndex) => {
    if (showTaskNumber) return;
    setSelectedAnswer(answerIndex);
  };

  const handleNextTask = () => {
    if (selectedAnswer === null) return;

    const currentTask = tasks[currentTaskIndex];
    const isCorrect = selectedAnswer === currentTask.correctAnswer;

    setAnswers([...answers, {
      taskNumber: currentTask.taskNumber,
      userAnswer: selectedAnswer,
      correctAnswer: currentTask.correctAnswer,
      isCorrect
    }]);

    setSelectedAnswer(null);

    if (currentTaskIndex + 1 >= tasks.length) {
      handleSubmitGame();
    } else {
      setCurrentTaskIndex(currentTaskIndex + 1);
    }
  };

  const handleSubmitGame = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const timeSpent = Math.floor((Date.now() - gameStartTime) / 1000);
    const correctCount = answers.filter(a => a.isCorrect).length;
    const finalScore = (correctCount / totalTasks) * 100;

    const gameData = {
      level,
      time_limit_minutes: timeLimit || 0,
      score_multiplier: 1.0,
      final_score: finalScore,
      correct_count: correctCount,
      total_tasks: totalTasks,
      time_spent_seconds: timeSpent,
      tasks_data: answers
    };

    try {
      const response = await apiClient.post(`/api/courses/ppt-game/runs/`, {
        subject: subjectId,
        ...gameData
      });

      navigate(`${gamesBase}/ppt/${subjectId}/results`, {
        state: {
          runId: response.id,
          ...gameData,
          subject
        }
      });
    } catch (error) {
      console.error('Failed to submit game:', error);
      alert('Failed to save game results. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleExit = () => {
    if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
      navigate(`${gamesBase}/ppt/${subjectId}`);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (tasks.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading tasks...</div>
      </div>
    );
  }

  const currentTask = tasks[currentTaskIndex];
  const progress = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleExit}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="text-sm">
              <span className="text-gray-400">Task</span>
              <span className="ml-2 font-bold text-white">{currentTaskIndex + 1} / {tasks.length}</span>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            {timeRemaining !== null && (
              <div className={`text-sm ${timeRemaining < 60 ? 'text-red-400' : 'text-gray-400'}`}>
                <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
              </div>
            )}
            <div className="text-sm text-gray-400">
              Level {level}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="max-w-7xl mx-auto mt-2">
          <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        {showTaskNumber ? (
          <div className="text-center">
            <div className="text-6xl font-bold text-blue-400 mb-4">
              Task {currentTask.taskNumber}
            </div>
            <div className="text-gray-400">Get ready...</div>
          </div>
        ) : (
          <div className="max-w-6xl w-full">

            {/* 2D Unfolded Pattern */}
            <div className="bg-gray-800 rounded-lg p-3 sm:p-6 mb-4 sm:mb-6">
              <div className="flex justify-center">
                <UnfoldedCube pattern={currentTask.unfoldedPattern} size={65} />
              </div>
            </div>

            {/* 3D Options */}
            <div className="flex justify-center gap-2 sm:gap-4 mb-6">
              {currentTask.options.map((cube, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className="mb-1">
                    <Cube3D 
                      cube={cube} 
                      size={optionSize}
                      selected={selectedAnswer === index + 1}
                      onClick={() => handleAnswerSelect(index + 1)}
                    />
                  </div>
                  <button
                    onClick={() => handleAnswerSelect(index + 1)}
                    className={`w-12 h-12 rounded-lg font-bold text-lg transition-all ${
                      selectedAnswer === index + 1
                        ? 'bg-blue-500 text-white scale-110'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {index + 1}
                  </button>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                onClick={handleNextTask}
                disabled={selectedAnswer === null}
                className={`px-8 py-3 rounded-lg font-semibold text-lg transition-all ${
                  selectedAnswer === null
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {currentTaskIndex + 1 >= tasks.length ? 'Finish Game' : 'Next Task'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PPTGame;
